const chapter = {
  id: 'locomotion-movement',
  slug: 'locomotion-movement',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Locomotion and Movement',
        hindi: 'गमन और संचलन',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'movements',
      title: {
        english: 'Types of Movement',
        hindi: 'गति के प्रकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Amoeboid** — by pseudopodia (cytoskeleton remodel). E.g. Amoeba, macrophages, leucocytes.',
        hindi: '**अमीबॉइड** - स्यूडोपोडिया (साइटोस्केलेटन रीमॉडेल) द्वारा। जैसे अमीबा, मैक्रोफेज, ल्यूकोसाइट्स।',
            },
            {
              english: '**Ciliary** — by cilia. Inner trachea (mucus & dust), Fallopian tubes (ovum), Paramoecium.',
        hindi: '**सिलिअरी** - सिलिया द्वारा। आंतरिक श्वासनली (बलगम और धूल), फैलोपियन ट्यूब (अंडाणु), पैरामीशियम।',
            },
            {
              english: '**Muscular** — by contraction of muscle fibres. Used in locomotion of complex animals.',
        hindi: '**मांसपेशी** - मांसपेशीय तंतुओं के संकुचन द्वारा। जटिल जानवरों की गति में उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'muscles',
      title: {
        english: 'Types of Muscle',
        hindi: 'पेशी के प्रकार',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'Skeletal (Striated)', 'Smooth (Visceral)', 'Cardiac'],
          rows: [
            ['Location', 'Attached to bones', 'Walls of viscera, blood vessels', 'Heart wall'],
            ['Striations', 'Present', 'Absent', 'Present'],
            ['Nuclei', 'Multinucleate', 'Uninucleate', 'Uninucleate (some bi-)'],
            ['Control', 'Voluntary', 'Involuntary', 'Involuntary'],
            ['Intercalated discs', 'Absent', 'Absent', 'Present'],
            ['Fatigue', 'Yes', 'No', 'No'],
          ],
        },
      ],
    },
    {
      id: 'skeletal-muscle',
      title: {
        english: 'Structure of Skeletal Muscle',
        hindi: 'कंकाल पेशी की संरचना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Muscle → fascicles → muscle fibres (cells; multinucleate, syncytium) → myofibrils → sarcomeres.',
        hindi: 'मांसपेशी → फासिकल्स → मांसपेशी फाइबर (कोशिकाएं; मल्टीन्यूक्लिएट, सिंकाइटियम) → मायोफिब्रिल्स → सार्कोमेरेस।',
            },
            {
              english: 'Plasma membrane = **sarcolemma**; cytoplasm = **sarcoplasm**; ER = **sarcoplasmic reticulum (SR — Ca²⁺ store)**.',
        hindi: 'प्लाज्मा झिल्ली = **सरकोलेममा**; साइटोप्लाज्म = **सार्कोप्लाज्म**; ईआर = **सार्कोप्लाज्मिक रेटिकुलम (एसआर - सीए²⁺ स्टोर)**।',
            },
            {
              english: '**Sarcomere** = functional unit, between two **Z-lines (discs)**. Has:',
        hindi: '**सरकोमेरे** = कार्यात्मक इकाई, दो **जेड-लाइनों (डिस्क)** के बीच। है:',
            },
            {
              english: '• **A-band (anisotropic, dark)** — full length of myosin (thick filament).',
        hindi: '• **ए-बैंड (एनिसोट्रोपिक, डार्क)** - मायोसिन की पूरी लंबाई (मोटा फिलामेंट)।',
            },
            {
              english: '• **I-band (isotropic, light)** — actin (thin filament) only; bisected by Z-line.',
        hindi: '• **आई-बैंड (आइसोट्रोपिक, लाइट)** - केवल एक्टिन (पतला फिलामेंट); Z-रेखा द्वारा विभाजित।',
            },
            {
              english: '• **H-zone** — central A-band; only myosin (no overlap).',
        hindi: '• **एच-ज़ोन** — केंद्रीय ए-बैंड; केवल मायोसिन (कोई ओवरलैप नहीं)।',
            },
            {
              english: '• **M-line** — middle of H-zone.',
        hindi: '• **एम-लाइन** — एच-ज़ोन का मध्य।',
            },
            {
              english: '**Thick filament (myosin)** — myosin has tail + globular head (cross-bridge) with ATPase activity.',
        hindi: '**मोटा फिलामेंट (मायोसिन)** - मायोसिन में ATPase गतिविधि के साथ पूंछ + गोलाकार सिर (क्रॉस-ब्रिज) होता है।',
            },
            {
              english: '**Thin filament (actin)** — F-actin polymer of G-actin + tropomyosin (covers myosin-binding sites at rest) + **troponin** (Ca²⁺ binds here).',
        hindi: '**पतला फिलामेंट (एक्टिन)** - जी-एक्टिन का एफ-एक्टिन पॉलिमर + ट्रोपोमायोसिन (आराम के समय मायोसिन-बाध्यकारी साइटों को कवर करता है) + **ट्रोपोनिन** (Ca²⁺ यहां बांधता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'contraction',
      title: {
        english: 'Sliding Filament Theory',
        hindi: 'सर्पण तन्तु सिद्धान्त',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**H.E. Huxley & A.F. Huxley (1954)** — actin slides over myosin during contraction; filament lengths unchanged.',
        hindi: '**एच.ई. हक्सले और ए.एफ. हक्सले (1954)** - संकुचन के दौरान एक्टिन मायोसिन पर फिसलता है; फिलामेंट की लंबाई अपरिवर्तित रहती है।',
            },
            {
              english: 'Steps:',
        hindi: 'कदम:',
            },
            {
              english: '1. Motor neuron releases **acetylcholine** at neuromuscular junction → action potential along sarcolemma → into T-tubules.',
        hindi: '1. मोटर न्यूरॉन न्यूरोमस्कुलर जंक्शन पर **एसिटाइलकोलाइन** छोड़ता है → सरकोलेममा के साथ ऐक्शन पोटेंशिअल → टी-ट्यूब्यूल में।',
            },
            {
              english: '2. SR releases **Ca²⁺** into sarcoplasm.',
        hindi: '2. SR सार्कोप्लाज्म में **Ca²⁺** छोड़ता है।',
            },
            {
              english: '3. Ca²⁺ binds **troponin** → conformational change → tropomyosin moves → actin\'s myosin-binding sites exposed.',
        hindi: '3. Ca²⁺ **ट्रोपोनिन** को बांधता है → गठनात्मक परिवर्तन → ट्रोपोमायोसिन चलता है → एक्टिन की मायोसिन-बाध्यकारी साइटें उजागर होती हैं।',
            },
            {
              english: '4. Myosin head (energised by ATP hydrolysis) binds actin → **cross-bridge** → power stroke (head pivots) → sarcomere shortens.',
        hindi: '4. मायोसिन हेड (एटीपी हाइड्रोलिसिस द्वारा सक्रिय) एक्टिन को बांधता है → **क्रॉस-ब्रिज** → पावर स्ट्रोक (हेड पिवोट्स) → सार्कोमियर छोटा करता है।',
            },
            {
              english: '5. New ATP binds myosin → cross-bridge releases → cycle repeats.',
        hindi: '5. नया एटीपी मायोसिन को बांधता है → क्रॉस-ब्रिज रिलीज → चक्र दोहराता है।',
            },
            {
              english: '6. Relaxation: Ca²⁺ pumped back to SR (active); tropomyosin re-covers sites.',
        hindi: '6. विश्राम: Ca²⁺ को वापस SR (सक्रिय) में पंप किया गया; ट्रोपोमायोसिन साइटों को पुनः कवर करता है।',
            },
            {
              english: 'During contraction: **A-band length unchanged**; I-band & H-zone shorten; Z-lines come closer.',
        hindi: 'संकुचन के दौरान: **ए-बैंड की लंबाई अपरिवर्तित**; आई-बैंड और एच-ज़ोन छोटा; Z-लाइनें करीब आती हैं।',
            },
            {
              english: '**Energy** — ATP from creatine phosphate (immediate), then anaerobic glycolysis (lactic acid → muscle fatigue), then aerobic respiration.',
        hindi: '**ऊर्जा** - क्रिएटिन फॉस्फेट (तत्काल) से एटीपी, फिर एनारोबिक ग्लाइकोलाइसिस (लैक्टिक एसिड → मांसपेशियों की थकान), फिर एरोबिक श्वसन।',
            },
            {
              english: '**Red fibres** — myoglobin-rich, mitochondria-rich; aerobic; slow-twitch; fatigue-resistant.',
        hindi: '**लाल रेशे** - मायोग्लोबिन-समृद्ध, माइटोकॉन्ड्रिया-समृद्ध; एरोबिक; धीमी गति से हिलना; थकान प्रतिरोधी.',
            },
            {
              english: '**White fibres** — less myoglobin, fewer mitochondria; anaerobic; fast-twitch; quick fatigue.',
        hindi: '**सफ़ेद रेशे** - कम मायोग्लोबिन, कम माइटोकॉन्ड्रिया; अवायवीय; तेजी से हिलना; त्वरित थकान.',
            },
          ],
        },
      ],
    },
    {
      id: 'skeleton',
      title: {
        english: 'Skeletal System',
        hindi: 'कंकाल तंत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Adult human skeleton = 206 bones** (newborn ~270, fuse with growth).',
        hindi: '**वयस्क मानव skeleton = 206 bones** (newborn ~270, विकास के साथ जुड़ जाता है)।',
            },
            {
              english: '**Axial skeleton (80 bones)** — skull (22; cranial 8 + facial 14), ear ossicles (6 — malleus, incus, stapes ×2), hyoid (1), vertebral column (26), sternum (1), ribs (24).',
        hindi: '**अक्षीय कंकाल (80 हड्डियाँ)** - खोपड़ी (22; कपाल 8 + चेहरे की 14), कान की अस्थियाँ (6 - मैलियस, इनकस, स्टेप्स ×2), हाइपोइड (1), कशेरुक स्तंभ (26), उरोस्थि (1), पसलियाँ (24)।',
            },
            {
              english: '• **Vertebral formula**: cervical 7, thoracic 12, lumbar 5, sacral 1 (5 fused), coccygeal 1 (4 fused) = 26.',
        hindi: '• **कशेरुका सूत्र**: ग्रीवा 7, वक्ष 12, काठ 5, त्रिक 1 (5 जुड़े हुए), अनुमस्तिष्क 1 (4 जुड़े हुए) = 26।',
            },
            {
              english: '• Atlas (C1) supports skull; axis (C2) allows rotation.',
        hindi: '• एटलस (C1) खोपड़ी को सपोर्ट करता है; अक्ष (C2) घूर्णन की अनुमति देता है।',
            },
            {
              english: '• Ribs: 12 pairs. **True (1-7)** — joined to sternum directly. **False (8-10)** — joined via 7th rib. **Floating (11-12)** — not joined.',
        hindi: '• पसलियां: 12 जोड़े। **सही (1-7)** — सीधे उरोस्थि से जुड़ा हुआ। **झूठा (8-10)** — 7वीं पसली के माध्यम से जुड़ा हुआ। **फ्लोटिंग (11-12)** - जुड़ा हुआ नहीं। **परिशिष्ट कंकाल (126 हड्डियाँ)**:',
            },
            {
              english: '**Appendicular skeleton (126 bones)**:',
        hindi: '**परिशिष्ट कंकाल (126 हड्डियाँ)**:',
            },
            {
              english: '• **Pectoral girdle** (4) — clavicle + scapula (×2).',
        hindi: '• **पेक्टोरल मेर्डल** (4) — हंसली + स्कैपुला (×2)।',
            },
            {
              english: '• **Pelvic girdle** (2) — coxal bones (each = ilium + ischium + pubis).',
        hindi: '• **पेल्विक गर्डल** (2) — कॉक्सल हड्डियाँ (each = ilium + ischium + pubis)।',
            },
            {
              english: '• **Forelimb (60)** — humerus, radius, ulna, 8 carpals, 5 metacarpals, 14 phalanges (each side ×2).',
        hindi: '• **अग्रपाद (60)** - ह्यूमरस, त्रिज्या, उल्ना, 8 कार्पल, 5 मेटाकार्पल, 14 फालेंज (प्रत्येक पक्ष ×2)।',
            },
            {
              english: '• **Hindlimb (60)** — femur (longest), tibia, fibula, patella, 7 tarsals, 5 metatarsals, 14 phalanges (each side ×2).',
        hindi: '• **हिंडलिंब (60)** - फीमर (सबसे लंबी), टिबिया, फाइबुला, पटेला, 7 टार्सल, 5 मेटाटार्सल, 14 फालेंज (प्रत्येक पक्ष ×2)। **जोड़**:',
            },
            {
              english: '**Joints**:',
        hindi: '**जोड़**:',
            },
            {
              english: '• **Fibrous (immovable)** — skull sutures.',
        hindi: '• **रेशेदार (अचल)** - खोपड़ी के टांके।',
            },
            {
              english: '• **Cartilaginous (slightly movable)** — between vertebrae.',
        hindi: '• **कार्टिलाजिनस (थोड़ा चलने योग्य)** - कशेरुकाओं के बीच।',
            },
            {
              english: '• **Synovial (freely movable)** — synovial fluid; ball-and-socket (shoulder, hip), hinge (knee, elbow), pivot (atlas-axis), saddle (thumb), gliding (carpals), condyloid (wrist).',
        hindi: '• **श्लेष (स्वतंत्र रूप से चलने योग्य)** — श्लेष द्रव; बॉल-एंड-सॉकेट (कंधे, कूल्हे), काज (घुटना, कोहनी), धुरी (एटलस-अक्ष), सैडल (अंगूठा), ग्लाइडिंग (कार्पल), कंडीलॉइड (कलाई)।',
            },
          ],
        },
      ],
    },
    {
      id: 'disorders',
      title: {
        english: 'Disorders',
        hindi: 'विकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Myasthenia gravis** — autoimmune; antibodies against ACh receptors → muscle weakness.',
        hindi: '**मायस्थेनिया ग्रेविस** - स्वप्रतिरक्षी; एसीएच रिसेप्टर्स के खिलाफ एंटीबॉडी → मांसपेशियों की कमजोरी।',
            },
            {
              english: '**Muscular dystrophy** — progressive degeneration (e.g. Duchenne MD, X-linked).',
        hindi: '**मस्कुलर डिस्ट्रॉफी** - प्रगतिशील अध:पतन (उदाहरण के लिए डचेन एमडी, एक्स-लिंक्ड)।',
            },
            {
              english: '**Tetany** — rapid spasms from low Ca²⁺.',
        hindi: '**टेटनी** — कम Ca²⁺ से तीव्र ऐंठन।',
            },
            {
              english: '**Arthritis** — joint inflammation. Osteo (wear), Rheumatoid (autoimmune), Gouty (uric acid crystals).',
        hindi: '**गठिया** - जोड़ों की सूजन। ऑस्टियो (घिसाव), रुमेटॉइड (ऑटोइम्यून), गाउटी (यूरिक एसिड क्रिस्टल)।',
            },
            {
              english: '**Osteoporosis** — ↓ bone mass; ↓ oestrogen post-menopause.',
        hindi: '**ऑस्टियोपोरोसिस** — ↓ अस्थि द्रव्यमान; ↓ रजोनिवृत्ति के बाद एस्ट्रोजन।',
            },
            {
              english: '**Gout** — hyperuricaemia → urate crystals in joints.',
        hindi: '**गाउट** — हाइपरयुरिसीमिया → जोड़ों में यूरेट क्रिस्टल।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Adult skeleton} = 206\\ \\text{bones}',
      desc: {
        english: '',
        hindi: 'वयस्क कंकाल = 206\\ हड्डियाँ',
      },
    },
    {
      latex: '\\text{Vertebral formula: } 7+12+5+1+1 = 26',
      desc: {
        english: '',
        hindi: 'कशेरुक सूत्र: 7+12+5+1+1 = 26',
      },
    },
    {
      latex: '\\text{Sarcomere units: A-band, I-band, H-zone, Z-line, M-line}',
      desc: {
        english: '',
        hindi: 'सरकोमेरे इकाइयाँ: ए-बैंड, आई-बैंड, एच-ज़ोन, ज़ेड-लाइन, एम-लाइन',
      },
    },
    {
      latex: '\\text{Sliding filament: A unchanged; I, H shorten}',
      desc: {
        english: '',
        hindi: 'स्लाइडिंग फिलामेंट: एक अपरिवर्तित; आई, एच छोटा करें',
      },
    },
    {
      latex: 'Ca^{2+} \\to \\text{Troponin} \\to \\text{cross-bridge formation}',
      desc: {
        english: '',
        hindi: 'सीए 2+ ट्रोपोनिन क्रॉस-ब्रिज गठन',
      },
    },
    {
      latex: '\\text{Lactic acid in muscle} \\Rightarrow \\text{fatigue}',
      desc: {
        english: '',
        hindi: 'मांसपेशियों की थकान में लैक्टिक एसिड',
      },
    },
    {
      latex: '\\text{Red fibres: aerobic, myoglobin-rich; White fibres: anaerobic}',
      desc: {
        english: '',
        hindi: 'लाल रेशे: एरोबिक, मायोग्लोबिन युक्त; सफेद रेशे: अवायवीय',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Sarcomere',
      problem: {
        english: 'During muscle contraction, the band that does NOT change length is:',
        hindi: 'मांसपेशी संकुचन के दौरान, वह बैंड जो लंबाई नहीं बदलता है:',
      },
      answer: {
        english: 'A-band',
        hindi: 'ए-बैंड',
      },
      solution: {
        english: 'I-band and H-zone shorten; A-band is fixed.',
        hindi: 'आई-बैंड और एच-ज़ोन छोटा; ए-बैंड तय हो गया है.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Bones',
      problem: {
        english: 'Number of bones in adult human skeleton:',
        hindi: 'वयस्क मानव कंकाल में हड्डियों की संख्या:',
      },
      answer: {
        english: '206',
        hindi: '206',
      },
      solution: {
        english: '80 axial + 126 appendicular.',
        hindi: '80 अक्षीय + 126 परिशिष्ट।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Joint',
      problem: {
        english: 'Hip joint is an example of:',
        hindi: 'कूल्हे का जोड़ इसका एक उदाहरण है:',
      },
      answer: {
        english: 'Ball and socket joint',
        hindi: 'बॉल और सॉकेट जोड़',
      },
      solution: {
        english: 'Allows movement in all planes.',
        hindi: 'सभी विमानों में आवाजाही की अनुमति देता है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Muscle',
      problem: {
        english: 'Cross-bridges in muscle contraction are formed between:',
        hindi: 'मांसपेशियों के संकुचन में क्रॉस-ब्रिज किसके बीच बनते हैं:',
      },
      answer: {
        english: 'Myosin heads and actin',
        hindi: 'मायोसिन हेड्स और एक्टिन',
      },
      solution: {
        english: 'After Ca²⁺ exposes binding sites.',
        hindi: 'Ca²⁺ के बाद बाइंडिंग साइटों को उजागर करता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Disease',
      problem: {
        english: 'Myasthenia gravis is caused by:',
        hindi: 'मायस्थेनिया ग्रेविस किसके कारण होता है:',
      },
      answer: {
        english: 'Autoimmune attack on ACh receptors',
        hindi: 'एसीएच रिसेप्टर्स पर ऑटोइम्यून हमला',
      },
      solution: {
        english: 'Causes progressive muscle weakness.',
        hindi: 'मांसपेशियों में प्रगतिशील कमजोरी का कारण बनता है।',
      },
    },
  ],
  next: {
    slug: 'neural-control-coordination',
    title: 'Neural Control and Coordination',
  },
};

export default chapter;
